import { SHARED } from "./_shared.mjs";
import { startCapturingParents, stopCapturingParents } from "./capture.mjs";
import { GLOBAL_START_EPOCH } from "./constants.mjs";
import { attach, detach, haveParentsChanged } from "./helpers.mjs";
/**
 * An EffectScheduler is responsible for executing side effects in response to changes in state.
 *
 * You probably don't need to use this directly unless you're integrating this library with a framework of some kind.
 *
 * Instead, use the [[react]] and [[reactor]] functions.
 *
 * @example
 * ```ts
 * const render = new EffectScheduler('render', drawToCanvas)
 *
 * render.attach()
 * render.execute()
 * ```
 *
 * @public
 */ export class EffectScheduler {
    name;
    runEffect;
    _isActivelyListening = false;
    /**
   * Whether this scheduler is attached and actively listening to its parents.
   * @public
   */ // eslint-disable-next-line no-restricted-syntax
    get isActivelyListening() {
        return this._isActivelyListening;
    }
    /** @internal */ lastTraversedEpoch = GLOBAL_START_EPOCH;
    lastReactedEpoch = GLOBAL_START_EPOCH;
    _scheduleCount = 0;
    /**
   * The number of times this effect has been scheduled.
   * @public
   */ // eslint-disable-next-line no-restricted-syntax
    get scheduleCount() {
        return this._scheduleCount;
    }
    /** @internal */ parentEpochs = [];
    /** @internal */ parents = [];
    _scheduleEffect;
    constructor(name, runEffect, options){
        this.name = name;
        this.runEffect = runEffect;
        this._scheduleEffect = options?.scheduleEffect;
    }
    /** @internal */ maybeScheduleEffect() {
        // bail out if we have been cancelled by another effect
        if (!this._isActivelyListening) return;
        // bail out if no atoms have changed since the last time we ran this effect
        if (this.lastReactedEpoch === SHARED.globalEpoch) return;
        // bail out if we have parents and they have not changed since last time
        if (this.parents.length && !haveParentsChanged(this)) {
            this.lastReactedEpoch = SHARED.globalEpoch;
            return;
        }
        // if we don't have parents it's probably the first time this is running.
        this.scheduleEffect();
    }
    /** @internal */ scheduleEffect() {
        this._scheduleCount++;
        if (this._scheduleEffect) {
            // if the effect should be deferred (e.g. until a react render), do so
            this._scheduleEffect(this.maybeExecute);
        } else {
            // otherwise execute right now!
            this.execute();
        }
    }
    maybeExecute = ()=>{
        // bail out if we have been detached before this runs
        if (!this._isActivelyListening) return;
        this.execute();
    };
    /**
   * Makes this scheduler become 'actively listening' to its parents.
   * If it has been executed before it will immediately become eligible to receive 'maybeScheduleEffect' calls.
   * If it has not executed before it will need to be manually executed once to become eligible for scheduling, i.e. by calling [[EffectScheduler.execute]].
   * @public
   */ attach() {
        this._isActivelyListening = true;
        for(let i = 0, n = this.parents.length; i < n; i++){
            attach(this.parents[i], this);
        }
    }
    /**
   * Makes this scheduler stop 'actively listening' to its parents.
   * It will no longer be eligible to receive 'maybeScheduleEffect' calls until [[EffectScheduler.attach]] is called again.
   */ detach() {
        this._isActivelyListening = false;
        for(let i = 0, n = this.parents.length; i < n; i++){
            detach(this.parents[i], this);
        }
    }
    /**
   * Executes the effect immediately and returns the result.
   * @returns The result of the effect.
   */ execute() {
        try {
            startCapturingParents(this);
            const result = this.runEffect(this.lastReactedEpoch);
            this.lastReactedEpoch = SHARED.globalEpoch;
            return result;
        } finally{
            stopCapturingParents();
        }
    }
}
/**
 * Starts a new effect scheduler, scheduling the effect immediately.
 *
 * Returns a function that can be called to stop the scheduler.
 *
 * @example
 * ```ts
 * const color = atom('color', 'red')
 * const stop = react('set style', () => {
 *   divElem.style.color = color.get()
 * })
 * color.set('blue')
 * // divElem.style.color === 'blue'
 * stop()
 * color.set('green')
 * // divElem.style.color === 'blue'
 * ```
 *
 *
 * Also useful in React applications for running effects outside of the render cycle.
 *
 * @example
 * ```ts
 * useEffect(() => react('set style', () => {
 *   divRef.current.style.color = color.get()
 * }), [])
 * ```
 *
 * @public
 */ export function react(name, fn, options) {
    const scheduler = new EffectScheduler(name, fn, options);
    scheduler.attach();
    scheduler.scheduleEffect();
    return ()=>{
        scheduler.detach();
    };
}
/**
 * Creates a [[Reactor]], which is a thin wrapper around an [[EffectScheduler]].
 *
 * @public
 */ export function reactor(name, fn, options) {
    const scheduler = new EffectScheduler(name, fn, options);
    return {
        scheduler,
        start: (options)=>{
            const force = options?.force ?? false;
            scheduler.attach();
            if (force) {
                scheduler.scheduleEffect();
            } else {
                scheduler.maybeScheduleEffect();
            }
        },
        stop: ()=>{
            scheduler.detach();
        }
    };
}
